import React, { useEffect, useMemo, useState, useRef } from "react";
import { IconButton, TextField } from "@mui/material";
import SearchBar from "../../common/filters/SearchBar";
import DataTable from "../../common/DataTable";
import { api } from "../../api/api";
import { productInventoryEndPoints } from "../../constants/endpoints";
import editIcon from "../../assets/edit-icon.svg";
import Toast from "../../common/Toast";
import { SxProps } from "@mui/material";
import PageHeader from "../../common/PageHeader";
import downloadCloud from "../../assets/download-cloud-02.svg";
import { exportToExcel } from "../../utils/exportToExcel";
import {
  StyledCard,
  ButtonContainer,
  SaveButton,
  ExportButton,
  PaginationContainer,
  PaginationCaption,
  StyledPagination,
  HeaderTableRow,
  greyTextStyle,
} from "./InventoryDetails.styles";
import colors from "../../theme/Colours";

interface Inventory {
  id: number;
  productName: string;
  productId: string;
  inventoryId: number;
  quantity_20L: number;
  quantity_30L: number;
  quantity_50L: number;
  editIcon: string;
}

type BulkUpdatePayload = {
  quantity_20L?: number;
  quantity_30L?: number;
  quantity_50L?: number;
  productId: string;
  inventoryId: number;
};
interface Column {
  label: React.ReactNode;
  field: string;
  align?: "left" | "right" | "center";
  renderCell?: (row: Inventory) => React.ReactNode;
  labelStyle?: SxProps;
  cellStyle?: React.CSSProperties;
}

const InventoryDetails = () => {
  const [inventoryDataState, setInventoryDataState] = useState<Inventory[]>([]);
  const [bulkUpdatePayload, setBulkUpdatePayload] = useState<
    BulkUpdatePayload[]
  >([]);
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [tempQuantities, setTempQuantities] = useState<
    Record<number, Partial<Inventory>>
  >({});
  const [search, setSearch] = useState("");
  const [error, setError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    totalRecords: 0,
    totalPages: 1,
  });
  useEffect(() => {
    const loadInventoryData = async () => {
      setError(false);
      try {
        const response = await api.get(
          `${productInventoryEndPoints.productInventory}`,
          {
            params: {
              page: pagination.page,
              limit: pagination.rowsPerPage,
              search,
            },
            headers: {
              orgId: localStorage.getItem("organizationId"),
            },
          }
        );
        setInventoryDataState(response?.data?.data?.data);
        setPagination((prev) => ({
          ...prev,
          totalRecords: response?.data?.data?.totalRecords || 0,
          totalPages: response?.data?.data?.totalPages || 1,
        }));
      } catch (error) {
        console.error("Error fetching Inventory Items", error);
        setError(true);
        //setToastMessage("Failed to fetch Inventory Items");
        //setShowToast(true);
      }
    };
    loadInventoryData();
  }, [pagination.page, pagination.rowsPerPage, search]);

  // search handler for SearchBar
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleQuantityChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    list: Inventory,
    size: "20L" | "30L" | "50L"
  ) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      const newValue = value === "" ? 0 : Number(value);
      setTempQuantities((prev) => ({
        ...prev,
        [list.id]: {
          ...prev[list.id],
          [`quantity_${size}`]: newValue,
        },
      }));
      setBulkUpdatePayload((prev) => {
        const existingItem = prev.find(
          (entry) =>
            entry.productId === list.productId &&
            entry.inventoryId === list.inventoryId
        );

        if (existingItem) {
          return prev.map((entry) =>
            entry.productId === list.productId &&
            entry.inventoryId === list.inventoryId
              ? { ...entry, [`quantity_${size}`]: newValue }
              : entry
          );
        } else {
          return [
            ...prev,
            {
              productId: list.productId,
              inventoryId: list.inventoryId,
              quantity_20L: list.quantity_20L ?? 0,
              quantity_30L: list.quantity_30L ?? 0,
              quantity_50L: list.quantity_50L ?? 0,
              [`quantity_${size}`]: newValue,
            },
          ];
        }
      });
    }
  };

  const handleSave = async () => {
    setError(false);
    try {
      const response = await api.put(
        `${productInventoryEndPoints.productInventory}`,
        bulkUpdatePayload,
        {
          headers: {
            orgId: localStorage.getItem("organizationId"),
          },
        }
      );
      if (response?.data) {
        setInventoryDataState((prevData) =>
          prevData.map((item) =>
            tempQuantities[item.id]
              ? { ...item, ...tempQuantities[item.id] }
              : item
          )
        );
        setTempQuantities({});
        setToastMessage("Inventory updated successfully");
        setInventoryDataState((prev) =>
          prev.map((item) =>
            tempQuantities[item.id]
              ? { ...item, ...tempQuantities[item.id] }
              : item
          )
        );
        setBulkUpdatePayload([]);
        setTempQuantities({});
        setIsEditing(null);
      } else {
        console.error("Error updating inventory:", error);
        setError(true);
        setToastMessage("Failed to update inventory");
      }
    } catch (error) {
      console.error("Error updating inventory:", error);
      setError(true);
      setToastMessage("Failed to update inventory");
    }
    setShowToast(true);
  };

  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleExport = async () => {
    const response = await api.get(
      `${productInventoryEndPoints.productInventory}`,
      {
        params: {
          page: 1,
          limit: pagination.totalRecords,
        },
        headers: {
          orgId: localStorage.getItem("organizationId"),
        },
      }
    );

    const fullInventoryData: Inventory[] = response?.data?.data?.data || [];
    const excelData = fullInventoryData.map((inventoryDataState) => ({
      Id: inventoryDataState.id,
      ProductName: inventoryDataState.productName,
      ProductId: inventoryDataState.productId,
      Quantity_20L: inventoryDataState.quantity_20L,
      Quantity_30L: inventoryDataState.quantity_30L,
      Quantity_50L: inventoryDataState.quantity_50L,
      Total_Quantity_Liters:
        (inventoryDataState.quantity_20L || 0) * 20 +
        (inventoryDataState.quantity_30L || 0) * 30 +
        (inventoryDataState.quantity_50L || 0) * 50,
    }));

    await exportToExcel(excelData, "inventory_export.xlsx");
  };

  const commonLabelStyle: SxProps = {
    color: colors.tableFieldText,
    fontWeight: 400,
  };

  const columns: Column[] = [
    {
      label: "S.no",
      field: "id",
      align: "left",
      cellStyle: { width: "70px", fontWeight: "500" },
      labelStyle: commonLabelStyle,
    },
    {
      label: "Product Name",
      field: "productName",
      align: "left",
      cellStyle: { width: "90px", fontWeight: "500", ...greyTextStyle },
      labelStyle: commonLabelStyle,
    },
    {
      label: "Product Id",
      field: "productId",
      align: "center",
      cellStyle: { width: "100px", textAlign: "center", ...greyTextStyle },
      labelStyle: commonLabelStyle,
    },
    {
      label: "20L",
      field: "quantity_20L",
      align: "center",
      cellStyle: { width: "90px", textAlign: "center", ...greyTextStyle },
      labelStyle: commonLabelStyle,
    },
    {
      label: "30L",
      field: "quantity_30L",
      align: "center",
      cellStyle: { width: "90px", textAlign: "center", ...greyTextStyle },
      labelStyle: commonLabelStyle,
    },
    {
      label: "50L",
      field: "quantity_50L",
      align: "center",
      cellStyle: { width: "90px", textAlign: "center", ...greyTextStyle },
      labelStyle: commonLabelStyle,
    },
    {
      label: "Total Quantity (L)",
      field: "totalQuantity",
      align: "center",
      cellStyle: { width: "120px", textAlign: "center", ...greyTextStyle },
      labelStyle: commonLabelStyle,
    },
    {
      label: "Edit",
      field: "editIcon",
      align: "center",
      cellStyle: { width: "65px", textAlign: "center" },
      labelStyle: commonLabelStyle,
    },
  ];

  const inventoryTableData = useMemo(() => {
    const { page, rowsPerPage } = pagination;
    const rowStartIndex = (page - 1) * rowsPerPage;
    return inventoryDataState.map((list, idx) => {
      const currentQuantity20L = Number(
        isEditing === list.id &&
          tempQuantities[list.id]?.[`quantity_20L`] !== undefined
          ? tempQuantities[list.id][`quantity_20L`] || 0
          : list.quantity_20L || 0
      );
      const currentQuantity30L = Number(
        isEditing === list.id &&
          tempQuantities[list.id]?.[`quantity_30L`] !== undefined
          ? tempQuantities[list.id][`quantity_30L`] || 0
          : list.quantity_30L || 0
      );
      const currentQuantity50L = Number(
        isEditing === list.id &&
          tempQuantities[list.id]?.[`quantity_50L`] !== undefined
          ? tempQuantities[list.id][`quantity_50L`] || 0
          : list.quantity_50L || 0
      );

      const totalQuantity =
        currentQuantity20L * 20 +
        currentQuantity30L * 30 +
        currentQuantity50L * 50;

      return {
        id: rowStartIndex + idx + 1,
        productName: list.productName,
        productId: list.productId,
        quantity_20L:
          isEditing === list.id ? (
            <TextField
              value={
                tempQuantities[list.id]?.[`quantity_20L`] !== undefined
                  ? tempQuantities[list.id][`quantity_20L`]
                  : list[`quantity_20L`]
              }
              onChange={(e) => handleQuantityChange(e, list, "20L")}
              size="small"
              variant="outlined"
            />
          ) : (
            list.quantity_20L
          ),
        quantity_30L:
          isEditing === list.id ? (
            <TextField
              value={
                tempQuantities[list.id]?.[`quantity_30L`] !== undefined
                  ? tempQuantities[list.id][`quantity_30L`]
                  : list[`quantity_30L`]
              }
              onChange={(e) => handleQuantityChange(e, list, "30L")}
              size="small"
              variant="outlined"
            />
          ) : (
            list.quantity_30L
          ),
        quantity_50L:
          isEditing === list.id ? (
            <TextField
              value={
                tempQuantities[list.id]?.[`quantity_50L`] !== undefined
                  ? tempQuantities[list.id][`quantity_50L`]
                  : list[`quantity_50L`]
              }
              onChange={(e) => handleQuantityChange(e, list, "50L")}
              size="small"
              variant="outlined"
            />
          ) : (
            list.quantity_50L
          ),
        totalQuantity: totalQuantity,
        editIcon: (
          <IconButton
            data-testid={`edit-button-${list.id}`}
            onClick={() => {
              if (isEditing !== null) {
                // Save the changes of the previously edited product
                setInventoryDataState((prevData) =>
                  prevData.map((item) =>
                    item.id === isEditing
                      ? { ...item, ...tempQuantities[isEditing] }
                      : item
                  )
                );

                setBulkUpdatePayload((prev) => {
                  const editingItem = inventoryDataState.find(
                    (item) => item.id === isEditing
                  );
                  if (editingItem) {
                    return [
                      ...prev.filter(
                        (entry) => entry.productId !== editingItem.productId
                      ),
                      {
                        ...tempQuantities[isEditing],
                        productId: editingItem.productId,
                        inventoryId: editingItem.inventoryId,
                      },
                    ];
                  }
                  return prev;
                });
              }

              setIsEditing(list.id);

              setTempQuantities((prev) => ({
                ...prev,
                [list.id]: {
                  quantity_20L:
                    prev[list.id]?.quantity_20L ?? list.quantity_20L,
                  quantity_30L:
                    prev[list.id]?.quantity_30L ?? list.quantity_30L,
                  quantity_50L:
                    prev[list.id]?.quantity_50L ?? list.quantity_50L,
                },
              }));
            }}
          >
            <img src={editIcon} alt="Edit" />
          </IconButton>
        ),
      };
    });
  }, [inventoryDataState, isEditing, tempQuantities, pagination]);

  const { page, rowsPerPage, totalPages, totalRecords } = pagination;
  const startIndex = (+page - 1) * +rowsPerPage;
  const endIndex = Math.min(startIndex + +rowsPerPage, +totalRecords);

  return (
    <>
      <PageHeader title="Inventory" />
      <StyledCard>
        <Toast
          showToast={showToast}
          setShowToast={setShowToast}
          toastMessage={toastMessage}
          error={error}
        />
        <ButtonContainer>
          <SearchBar searchTerm={search} handleSearch={handleSearchChange} />
          {isEditing !== null && (
            <SaveButton variant="contained" onClick={handleSave}>
              Save
            </SaveButton>
          )}
          <ExportButton variant="contained" onClick={handleExport}>
            <img src={downloadCloud} alt="download csv" />
            Export
          </ExportButton>
        </ButtonContainer>
        <DataTable
          columns={columns}
          data={inventoryTableData}
          headerRowStyle={HeaderTableRow}
          emptyStateMessage="No Products available in inventory"
        />
        <PaginationContainer>
          <PaginationCaption variant="subtitle2">
            Showing data {startIndex + 1} to {endIndex} of {totalRecords}{" "}
            entries
          </PaginationCaption>
          <StyledPagination
            count={totalPages}
            page={+page}
            onChange={(event, value) => handleChangePage(value)}
            shape="rounded"
            variant="outlined"
          />
        </PaginationContainer>
      </StyledCard>
    </>
  );
};

export default InventoryDetails;
